/**
 * `/` — auto-mints a new pad (`POST /api/scratchpads`) then redirects to
 * `/p/:slug` (spec.md § Routing & pages: no landing screen). The skeleton shows
 * during the mint. On a mint failure we fall back to the not-found page so the
 * user always has a "Start a new pad" path.
 *
 * Web-created pads are seeded with a starter title heading ({@link NEW_PAD_BODY})
 * so a fresh pad opens with an editable `# Untitled Pad` first line. Pads created
 * directly via the API are NOT seeded — agents send their own body.
 *
 * React Router default-exports route components (AGENTS.md § Code style).
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPad } from "../lib/api";
import { NotFound } from "../components/NotFound";
import { PadSkeleton } from "../components/PadSkeleton";

/** Starter body for pads minted from the web (heading + an empty line to type in). */
const NEW_PAD_BODY = "# Untitled Pad\n\n";

export default function IndexRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  // StrictMode double-invokes effects in dev; guard so we mint exactly once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    async function mint(): Promise<void> {
      try {
        const created = await createPad(NEW_PAD_BODY);
        navigate(`/p/${created.slug}`, { replace: true });
      } catch {
        setFailed(true);
      }
    }

    void mint();
  }, [navigate]);

  if (failed) {
    return <NotFound />;
  }

  return <PadSkeleton />;
}
